import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, Teleport as _Teleport, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { id: "analytics-dashboard" }
const _hoisted_2 = { class: "top-container" }
const _hoisted_3 = { class: "filter-result" }

import UnderlineTabGroup from '@/design-system/molecules/UnderlineTabGroup.vue'
import { ref, watch, inject, computed, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { sharedDataKey } from '../../types'
import { useI18n } from 'vue-i18n'
import useFeatures, { useFeature } from '@/pinia/features'
import { ReportInformation, useResultReportNew } from '../useResultReport'
import FeatureComponent from '@/components/FeatureComponent.vue'
import { FeatureType, useCurrentFeatureFilters } from '@/composables/useFilters'
import { useStore } from 'vuex'
import { useMapboxFeatureState } from '@/pinia/mapboxFeatureState'
import { useMeasurements } from '../measurements/useMeasurements'
import useColoredEntities from '@/composables/useColoredEntities'
import { useSelectionStore } from '@/pinia/selectionNew'
import CollapseControllerNew from './CollapseController/CollapseControllerNew.vue'
import DashboardCalendarNew from './DashboardCalendarNew.vue'

type Props = {
  title: string
  showCalendar?: boolean
  defaultDateSelection: string
}

type Emits = {
  (e: 'toggleFullscreen'): void
  (e: 'closeDashboard'): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DashboardFeatureNew',
  props: {
    title: {},
    showCalendar: { type: Boolean, default: true },
    defaultDateSelection: {}
  },
  emits: ["toggleFullscreen", "closeDashboard"],
  setup(__props: any) {

const { locale } = useI18n()





const filters = useCurrentFeatureFilters()
const features = useFeatures()
const { replaceFeature, setFeature } = features
const { feature: sections } = useFeature('home/*/*/*/data')
const { feature: currentSection } = useFeature('home/*/*/*/data/*')
const { resetRecords } = useMapboxFeatureState()
const route = useRoute()
const router = useRouter()
const measurements = useMeasurements()
const data = computed(
  (): ReportInformation => ({
    numberOf: {
      COMPLEX: measurements.information?.entityLevels[0]?.length,
      REAL_ESTATE: measurements.information?.entityLevels[1]?.length,
      CONSTRUCTION: measurements.information?.entityLevels[2]?.length,
      LEVEL: measurements.information?.entityLevels[3]?.length,
      SPACE: measurements.information?.entityLevels[4]?.length
    }
  })
)
// const isLoading = computed(() => !submitted.value?.data || (submitted.value?.isLoading ?? true))

const sharedData = inject(sharedDataKey)

const store = useStore()
const coloredEntities = useColoredEntities()
// useTransferState(store)

const buttons = computed(() =>
  sections.value
    ? sections.value.children?.map((section) => ({
        text: section.title[locale.value],
        name: section.name,
        onClick: () => {
          if (section.name !== currentSection.value?.name) {
            resetRecords()
            coloredEntities.reset(true)
            replaceFeature(section.name)
            // information.fetch()
          }
        }
      }))
    : undefined
)

const isPresentingExpandedFilters = ref(false)
const isPresentingCalendar = ref(false)

const selection = useSelectionStore()

const { report } = useResultReportNew(data)

watch(
  () => selection.entitiesInPathByType['CONSTRUCTION'] ?? [],
  (constructions) => {
    filters.value?.$patch({
      current: {
        buildings: constructions
          .map(
            (construction) =>
              store.getters.allBuildings.find((b) => b.id === construction.id)?.properties
          )
          .filter(Boolean)
      }
    })
    filters.value?.submit()
  }
)

onUnmounted(() => {
  filters.value?.reset()
  filters.value?.submit()
  coloredEntities.reset(true)
  router.replace({ path: route.path, query: {} }).then(() => {
    const newQuery = { ...route.query }
    delete newQuery.components
    router.replace({ path: route.path, query: newQuery })
  })
})

watch(
  data,
  () => {
    if (data.value && 'numberOf' in data.value) {
      filters.value?.updateInformationResponse({
        buildings: data.value.numberOf?.CONSTRUCTION,
        rooms: 'SPACE' in data.value.numberOf ? data.value.numberOf.SPACE : undefined
      })
    }
  },
  { immediate: true }
)

const isMissingData = computed(() => {
  return (
    Object.keys(data.value && 'numberOf' in data.value ? data.value.numberOf : {}).every(
      (key) => data.value && 'numberOf' in data.value && data.value.numberOf[key] === 0
    ) &&
    data.value &&
    'fields' in data.value &&
    (data.value.fields as any[] | undefined)?.length === 0
  )
})

const isProjectAnalysis = computed(() => {
  return (
    filters.value?.current.type === FeatureType.ProjectAnalysisExternal ||
    filters.value?.current.type === FeatureType.ProjectAnalysisInternal
  )
})

const filtersResult = computed(() => {
  if (isProjectAnalysis.value && !filters.value?.submittedGeographicalFilters.complex) {
    if (locale.value === 'en') {
      return 'Results based on selection of - buildings'
    } else {
      return 'Resultat baserat på urval av - byggnader'
    }
  }

  if (filters.value?.submittedGeographicalFilters.room) {
    if (locale.value === 'en') {
      return 'Results based on selection of 1 building and 1 room'
    } else {
      return 'Resultat baserat på urval av 1 byggnad och 1 rum'
    }
  } else {
    return report.value
  }
})

watch(
  sections,
  () => {
    if (!sections.value?.parent) {
      return
    }
    const featureName = sections.value?.name
    const firstChildFeature = sections.value?.children?.[0]
    const currentSectionName = currentSection.value?.name
    if (featureName && firstChildFeature && !currentSectionName) {
      setFeature(firstChildFeature)
    }
  },
  { immediate: true }
)

const resetForm = () => {
  filters.value?.reset()
}
const submitForm = () => {
  isPresentingExpandedFilters.value = false
}
const submitCalendarForm = () => {
  filters.value?.submit()
  isPresentingCalendar.value = false
}
const computedClass = computed(() => {
  return {
    fullscreen: sharedData?.fullscreen
  }
})

watch(
  sections,
  (feature) => {
    if (feature) {
      filters.value?.submit()
    }
  },
  { immediate: true }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("section", _hoisted_3, [
        _createElementVNode("span", null, _toDisplayString(filtersResult.value), 1)
      ]),
      _createVNode(CollapseControllerNew, {
        isCollapsed: !isPresentingExpandedFilters.value,
        showCalendar: _ctx.showCalendar,
        defaultSelection: _ctx.defaultDateSelection,
        onPresentCalendar: _cache[0] || (_cache[0] = () => (isPresentingCalendar.value = true)),
        onToggleCollapse: _cache[1] || (_cache[1] = () => (isPresentingExpandedFilters.value = !isPresentingExpandedFilters.value))
      }, null, 8, ["isCollapsed", "showCalendar", "defaultSelection"])
    ]),
    _cache[3] || (_cache[3] = _createElementVNode("div", { class: "horizontal-divider" }, null, -1)),
    _createElementVNode("div", {
      class: _normalizeClass(["scrollable-container", computedClass.value])
    }, [
      _createVNode(FeatureComponent, {
        feature: "home/*/*/*/filters",
        information: _unref(measurements).information,
        isCollapsed: !isPresentingExpandedFilters.value,
        onResetForm: resetForm,
        onSubmitForm: submitForm
      }, null, 8, ["information", "isCollapsed"]),
      (buttons.value && buttons.value.length > 1)
        ? (_openBlock(), _createBlock(UnderlineTabGroup, {
            key: 0,
            buttons: buttons.value,
            size: 'small',
            style: 'margin-top: 16px;',
            "selected-name": _unref(route).path.split('/').pop()
          }, null, 8, ["buttons", "selected-name"]))
        : _createCommentVNode("", true),
      _createVNode(FeatureComponent, {
        isMissingData: isMissingData.value,
        isMissingFilter: !_unref(filters)?.submittedMeta.hasGeographicalFilter,
        feature: "home/*/*/*/data/*"
      }, null, 8, ["isMissingData", "isMissingFilter"])
    ], 2),
    (_openBlock(), _createBlock(_Teleport, { to: "#home" }, [
      (isPresentingCalendar.value)
        ? (_openBlock(), _createBlock(DashboardCalendarNew, {
            key: 0,
            onCalendarClose: _cache[2] || (_cache[2] = () => (isPresentingCalendar.value = false)),
            onCalendarSubmit: submitCalendarForm
          }))
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})